import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { ShieldCheck, ShieldAlert, Loader2 } from 'lucide-react';
import { api } from '../lib/api';

interface VatValidation {
  valid: boolean;
  countryCode?: string;
  name?: string;
  address?: string;
  checkedAt?: string;
}

/**
 * Inline VIES lookup widget for a vendor. Renders the current VAT ID
 * (if any) and a button to revalidate against the live VIES service via
 * `GET /tax-eu/validate/:vatId`. The server caches 30 days.
 */
export function VatIdValidator({ wsId, taxId }: { wsId: string; taxId: string | null }) {
  const { t } = useTranslation();
  const [result, setResult] = useState<VatValidation | null>(null);
  const validate = useMutation({
    mutationFn: async () => {
      if (!taxId) throw new Error('missing-vat-id');
      const r = await api.get<VatValidation>(`/workspaces/${wsId}/tax-eu/validate/${encodeURIComponent(taxId)}`);
      return r.data;
    },
    onSuccess: (data) => setResult(data),
  });

  if (!taxId) {
    return <span className="text-ink-400">—</span>;
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="font-mono text-sm">{taxId}</span>
      <button
        type="button"
        onClick={() => validate.mutate()}
        disabled={validate.isPending}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-ink-300 dark:border-ink-700 text-[11px] hover:bg-ink-50 dark:hover:bg-ink-900 disabled:opacity-50"
      >
        {validate.isPending ? <Loader2 size={10} className="animate-spin" /> : <ShieldCheck size={10} />}
        {t('vendors.drawer.validateVat')}
      </button>
      {result && (
        <span
          className={`inline-flex items-center gap-1 text-[11px] ${
            result.valid ? 'text-emerald-700' : 'text-warn-600'
          }`}
        >
          {result.valid ? <ShieldCheck size={11} /> : <ShieldAlert size={11} />}
          {result.valid ? t('vendors.drawer.vatValid') : t('vendors.drawer.vatInvalid')}
          {result.valid && result.name ? ` · ${result.name}` : ''}
        </span>
      )}
      {validate.isError && (
        <span className="text-[11px] text-warn-600">{t('vendors.drawer.vatLookupFailed')}</span>
      )}
    </div>
  );
}
